import {
  GREEN_450,
  MOON_200,
  RED_400,
  RED_500,
} from '@wandb/weave/common/css/color.styles';
import {THEME} from '@wandb/weave/common/css/globals.styles';
import {
  TIME_RANGE_CONFIG,
  type UsageTimeRangeMode,
} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/pages/wfReactInterface/tsDataModelHooks';
import React, {useMemo, useState} from 'react';
import {
  type AxisDomainItem,
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {
  CHART_HEIGHT,
  ChartStatus,
  METRIC_LINE_COLOR,
  TimeRangeSelector,
  type WeaveMetricChartProps,
} from './chartShared';
import {isSpanErrorRate, useSpanAlertChartData} from './useSpanAlertChartData';
import {formatTime, type TimeRange} from './useWeaveMetricChartData';

const RANGE_TO_MODE: Record<TimeRange, UsageTimeRangeMode> = {
  '1h': 'hour',
  '1d': 'day',
  '1w': 'week',
};

/**
 * Spans-source metric chart. Error metrics render the full population that
 * entered each window as a stacked bar (errors red over OK spans green), so a
 * bucket's height is the number of spans considered, not just the hits.
 * Numeric metrics (latency, tokens) render as a single line, since they have
 * no match/total split.
 */
export const SpanMetricChart: React.FC<WeaveMetricChartProps> = ({
  entityName,
  projectName,
  config,
}) => {
  const [range, setRange] = useState<TimeRange>('1h');
  const {metricFilter} = config;
  const metricPath = metricFilter.metricPath ?? '';
  const aggFn = metricFilter.aggregationFunction ?? 'mean';
  const threshold = parseThreshold(metricFilter.threshold);
  const isError = isSpanErrorRate(metricPath);
  // Error metrics are restricted to percentage/count aggregation, so any
  // non-percentage error value here is a raw count (see extractBucket).
  const isPercentage = aggFn === 'percentage';

  const {durationMs, granularitySeconds} =
    TIME_RANGE_CONFIG[RANGE_TO_MODE[range]];
  const {startMs, endMs} = useMemo(() => {
    // oxlint-disable-next-line react/purity
    const end = Date.now();
    return {startMs: end - durationMs, endMs: end};
  }, [durationMs]);

  const {buckets, loading, error, hasData} = useSpanAlertChartData({
    projectId: `${entityName}/${projectName}`,
    metricFilter,
    startMs,
    endMs,
    granularitySeconds,
    enabled: true,
  });

  const rows = useMemo<StackedRow[]>(
    () =>
      buckets.map(b => {
        const total = b.totalCount ?? 0;
        const matched = b.matchedCount ?? 0;
        return {
          formattedTime: formatTime(b.timestamp, range),
          matched,
          happy: Math.max(0, total - matched),
          total,
          value: b.value,
        };
      }),
    [buckets, range]
  );

  // The threshold sits on the count axis, except for error percentage, where it
  // is a rate on the hidden right axis so the population bars keep their scale.
  const thresholdOnPrimary = threshold != null && !(isError && isPercentage);
  const primaryDomain: [AxisDomainItem, AxisDomainItem] = thresholdOnPrimary
    ? [0, (dataMax: number) => Math.max(dataMax, (threshold ?? 0) * 1.1)]
    : [0, 'auto'];

  const shortMetric = metricPath.split('.').pop() ?? metricPath;

  return (
    <div>
      <TimeRangeSelector range={range} setRange={setRange} />
      <div style={{height: CHART_HEIGHT}}>
        <ChartStatus loading={loading} error={error} hasData={hasData}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={rows}
              margin={{top: 4, right: 8, bottom: 0, left: -16}}
              barCategoryGap="10%">
              <CartesianGrid
                yAxisId="primary"
                stroke={`light-dark(${MOON_200}, ${THEME.border})`}
                strokeDasharray="3 3"
              />
              <XAxis
                dataKey="formattedTime"
                tick={{fontSize: 10}}
                tickLine={false}
                axisLine={{stroke: THEME.border}}
                interval="preserveStartEnd"
              />
              <YAxis
                yAxisId="primary"
                tick={{fontSize: 10}}
                tickLine={false}
                axisLine={{stroke: THEME.border}}
                width={40}
                domain={primaryDomain}
                allowDecimals={!isError}
              />
              {isError && isPercentage && (
                <YAxis
                  yAxisId="rate"
                  orientation="right"
                  domain={[0, 100]}
                  hide
                />
              )}
              <Tooltip
                content={
                  <SpanMetricTooltip
                    isError={isError}
                    isPercentage={isPercentage}
                    shortMetric={shortMetric}
                    threshold={threshold}
                  />
                }
              />
              {isError ? (
                <>
                  <Bar
                    yAxisId="primary"
                    dataKey="matched"
                    stackId="pop"
                    name="Errors"
                    fill={RED_400}
                    isAnimationActive={false}
                  />
                  <Bar
                    yAxisId="primary"
                    dataKey="happy"
                    stackId="pop"
                    name="OK"
                    fill={GREEN_450}
                    isAnimationActive={false}
                  />
                </>
              ) : (
                <Line
                  yAxisId="primary"
                  type="monotone"
                  dataKey="value"
                  name={shortMetric}
                  stroke={METRIC_LINE_COLOR}
                  strokeWidth={2}
                  dot={false}
                  isAnimationActive={false}
                  connectNulls
                />
              )}
              {isError && isPercentage && (
                <Line
                  yAxisId="rate"
                  type="monotone"
                  dataKey="value"
                  name="Error rate"
                  stroke={METRIC_LINE_COLOR}
                  strokeWidth={1}
                  dot={false}
                  isAnimationActive={false}
                  connectNulls
                />
              )}
              {threshold != null && (
                <ReferenceLine
                  yAxisId={thresholdOnPrimary ? 'primary' : 'rate'}
                  y={threshold}
                  stroke={RED_500}
                  strokeDasharray="6 3"
                  label={{
                    value: `Threshold: ${threshold}`,
                    position: 'insideTopLeft',
                    fontSize: 11,
                    fontWeight: 600,
                    fill: RED_500,
                  }}
                />
              )}
              <Legend
                wrapperStyle={{fontSize: 11}}
                iconType="square"
                itemSorter={null}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </ChartStatus>
      </div>
    </div>
  );
};

type StackedRow = {
  formattedTime: string;
  matched: number;
  happy: number;
  total: number;
  value: number | null;
};

const parseThreshold = (raw: string | undefined): number | null => {
  if (raw == null || raw === '') {
    return null;
  }
  const parsed = parseFloat(raw);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatNum = (value: number): string | number =>
  Number.isInteger(value) ? value : parseFloat(value.toPrecision(4));

type SpanMetricTooltipProps = {
  isError: boolean;
  isPercentage: boolean;
  shortMetric: string;
  threshold: number | null;
  active?: boolean;
  label?: string | number;
  payload?: Array<{payload: StackedRow}>;
};

const SpanMetricTooltip: React.FC<SpanMetricTooltipProps> = ({
  isError,
  isPercentage,
  shortMetric,
  threshold,
  active,
  label,
  payload,
}) => {
  const row = payload?.[0]?.payload;
  if (!active || !row) {
    return null;
  }
  return (
    <div className="rounded border border-wb-border bg-wb-surface px-8 py-4 text-xs">
      <div className="mb-2 text-moon-500 dark:text-wb-text-tertiary">
        {label}
      </div>
      {isError ? (
        <div className="flex flex-col gap-2">
          <TooltipSwatch color={RED_400} label="Errors" value={row.matched} />
          <TooltipSwatch color={GREEN_450} label="OK" value={row.happy} />
          <div className="text-moon-500 dark:text-wb-text-tertiary">
            Total: {row.total}
          </div>
          {row.value != null && (
            <div className="text-moon-700 dark:text-wb-text-secondary">
              {isPercentage
                ? `${formatNum(row.value)}% error rate`
                : `${row.value} errors`}
            </div>
          )}
        </div>
      ) : (
        <div>
          <span className="font-semibold" style={{color: METRIC_LINE_COLOR}}>
            {row.value != null ? formatNum(row.value) : '—'}
          </span>{' '}
          <span className="text-moon-500 dark:text-wb-text-tertiary">
            {shortMetric}
          </span>
        </div>
      )}
      {threshold != null && (
        <div className="mt-2 text-moon-500 dark:text-wb-text-tertiary">
          Threshold: {threshold}
        </div>
      )}
    </div>
  );
};

const TooltipSwatch: React.FC<{
  color: string;
  label: string;
  value: number;
}> = ({color, label, value}) => (
  <div className="flex items-center gap-4">
    <span
      className="inline-block h-8 w-8 rounded-sm"
      style={{backgroundColor: color}}
    />
    <span className="text-moon-700 dark:text-wb-text-secondary">
      {label}: {value}
    </span>
  </div>
);
